// Schedules de transición — "% de kcal diarias que vienen de comida MyL"
// día a día, según la dieta previa del perro y posibles modificadores
// (cachorro / senior / patología digestiva / heces blandas / cambio de
// proteína simultáneo).
//
// Los schedules base salen de la guía nutricional de Patitas&Co
// (2026-04). Son universales: el % aplica sobre las kcal/día calculadas
// por la fórmula MyL para cada perro. La cantidad real de comida MyL
// en gramos = kcal_dia_total × pct/100 / kcal_per_100g_receta × 100.

export type CurrentFoodType = 'pienso_seco' | 'humeda_comercial' | 'barf' | 'cruda' | 'cocinada_otra';

export interface TransitionDay {
  /** 1-indexed: día 1 = primer día del pack iniciación. */
  day: number;
  /** Porcentaje (0-100) de kcal diarias que vienen de MyL ese día. */
  pctMyL: number;
}

export interface TransitionModifiers {
  /** < 12 meses */
  isCachorro?: boolean;
  /** ≥ 7 años */
  isSenior?: boolean;
  /** IBD, gastritis, colitis crónica */
  hasDigestivePathology?: boolean;
  /** Heces blandas/diarrea crónica reportada */
  hasChronicSoftStools?: boolean;
  /** El perro va a cambiar de proteína a la vez que de marca */
  proteinChange?: boolean;
}

// ── Schedules base ─────────────────────────────────────────────────────────

const BASE_SCHEDULES: Record<CurrentFoodType, TransitionDay[]> = {
  // 1. Pienso seco → Cocinada MyL · 10 días 🔴 (el caso más exigente)
  pienso_seco: [
    { day: 1, pctMyL: 25 },
    { day: 2, pctMyL: 25 },
    { day: 3, pctMyL: 50 },
    { day: 4, pctMyL: 50 },
    { day: 5, pctMyL: 75 },
    { day: 6, pctMyL: 75 },
    { day: 7, pctMyL: 75 },
    { day: 8, pctMyL: 100 },
    { day: 9, pctMyL: 100 },
    { day: 10, pctMyL: 100 },
  ],
  // 2. Húmeda comercial → Cocinada MyL · 5 días 🟢
  humeda_comercial: [
    { day: 1, pctMyL: 50 },
    { day: 2, pctMyL: 50 },
    { day: 3, pctMyL: 75 },
    { day: 4, pctMyL: 75 },
    { day: 5, pctMyL: 100 },
  ],
  // 3. BARF → Cocinada MyL · 7 días 🟡
  barf: [
    { day: 1, pctMyL: 25 },
    { day: 2, pctMyL: 25 },
    { day: 3, pctMyL: 50 },
    { day: 4, pctMyL: 50 },
    { day: 5, pctMyL: 75 },
    { day: 6, pctMyL: 75 },
    { day: 7, pctMyL: 100 },
  ],
  // 4. Cruda no-BARF → Cocinada MyL · 5 días 🟡
  cruda: [
    { day: 1, pctMyL: 50 },
    { day: 2, pctMyL: 50 },
    { day: 3, pctMyL: 75 },
    { day: 4, pctMyL: 75 },
    { day: 5, pctMyL: 100 },
  ],
  // 5. Cocinada otra marca → Cocinada MyL · 3 días 🟢
  cocinada_otra: [
    { day: 1, pctMyL: 50 },
    { day: 2, pctMyL: 50 },
    { day: 3, pctMyL: 100 },
  ],
};

// ── Modifiers ──────────────────────────────────────────────────────────────

/** Cachorro acorta el schedule en 2 días, recortando los días intermedios.
 *  Mínimo 7 días si viene de pienso seco (caso más sensible).
 */
function shrinkScheduleByDays(schedule: TransitionDay[], daysToRemove: number, minDays: number): TransitionDay[] {
  if (daysToRemove <= 0) return schedule;
  const targetLen = Math.max(minDays, schedule.length - daysToRemove);
  if (schedule.length <= targetLen) return schedule;
  // Para acortar: re-mapeamos el % de cada día original al nuevo índice (interpolación lineal).
  const out: TransitionDay[] = [];
  for (let i = 0; i < targetLen; i++) {
    const srcIdx = Math.round((i / (targetLen - 1)) * (schedule.length - 1));
    out.push({ day: i + 1, pctMyL: schedule[srcIdx].pctMyL });
  }
  return out;
}

/** Senior añade N días al final manteniendo el último escalón anterior antes
 *  del 100%, para suavizar la última fase. Default +4 (medio del rango 3-5). */
function extendForSenior(schedule: TransitionDay[], extraDays: number): TransitionDay[] {
  if (extraDays <= 0 || schedule.length === 0) return schedule;
  const out = schedule.map((d) => ({ ...d }));
  // Encuentra el último escalón antes del 100% (el penúltimo % distinto del 100)
  const last100Idx = out.findIndex((d) => d.pctMyL >= 100);
  if (last100Idx <= 0) return out;
  const prevPct = out[last100Idx - 1].pctMyL;
  // Inserta `extraDays` días con prevPct antes del primer 100%.
  const insertion: TransitionDay[] = [];
  for (let i = 0; i < extraDays; i++) {
    insertion.push({ day: 0, pctMyL: prevPct }); // re-numeraremos después
  }
  out.splice(last100Idx, 0, ...insertion);
  return out.map((d, i) => ({ day: i + 1, pctMyL: d.pctMyL }));
}

/** Patología digestiva: duplica la duración total y reparte en escalones
 *  de 12.5% en lugar de 25% (8 niveles vs 4). */
function applyDigestivePathology(schedule: TransitionDay[]): TransitionDay[] {
  if (schedule.length === 0) return schedule;
  const totalDays = schedule.length * 2;
  // 8 escalones equiespaciados de 12.5% hasta 100%.
  const stops = [12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100];
  const out: TransitionDay[] = [];
  for (let i = 0; i < totalDays; i++) {
    const stopIdx = Math.min(stops.length - 1, Math.floor((i * stops.length) / totalDays));
    out.push({ day: i + 1, pctMyL: stops[stopIdx] });
  }
  return out;
}

/** Heces blandas crónicas: +3 días al final del primer escalón (suaviza el
 *  inicio). Probiótico se añade como flag aparte (UI/email). */
function extendForSoftStools(schedule: TransitionDay[]): TransitionDay[] {
  if (schedule.length === 0) return schedule;
  const firstPct = schedule[0].pctMyL;
  const insertion: TransitionDay[] = [];
  for (let i = 0; i < 3; i++) {
    insertion.push({ day: 0, pctMyL: firstPct });
  }
  const out = [...insertion, ...schedule];
  return out.map((d, i) => ({ day: i + 1, pctMyL: d.pctMyL }));
}

/** Cambio de proteína: +2 días repitiendo el primer escalón. */
function extendForProteinChange(schedule: TransitionDay[]): TransitionDay[] {
  if (schedule.length === 0) return schedule;
  const firstPct = schedule[0].pctMyL;
  const insertion: TransitionDay[] = [];
  for (let i = 0; i < 2; i++) {
    insertion.push({ day: 0, pctMyL: firstPct });
  }
  const out = [...insertion, ...schedule];
  return out.map((d, i) => ({ day: i + 1, pctMyL: d.pctMyL }));
}

/**
 * Devuelve el schedule final aplicando modificadores. Orden de aplicación:
 *   1. Patología digestiva (transformación más fuerte → primero).
 *   2. Senior (+ días al final).
 *   3. Heces blandas (+ días al inicio).
 *   4. Cambio de proteína (+ días al inicio).
 *   5. Cachorro (recorte total — al final, respeta el min).
 */
export function getTransitionSchedule(baseFood: CurrentFoodType, modifiers: TransitionModifiers = {}): TransitionDay[] {
  let s = BASE_SCHEDULES[baseFood].map((d) => ({ ...d }));
  if (modifiers.hasDigestivePathology) s = applyDigestivePathology(s);
  if (modifiers.isSenior) s = extendForSenior(s, 4);
  if (modifiers.hasChronicSoftStools) s = extendForSoftStools(s);
  if (modifiers.proteinChange) s = extendForProteinChange(s);
  if (modifiers.isCachorro) {
    const minDays = baseFood === 'pienso_seco' ? 7 : 3;
    s = shrinkScheduleByDays(s, 2, minDays);
  }
  return s;
}

/**
 * Total de kcal MyL durante el periodo de transición.
 * El cliente pagará/recibirá esta cantidad — NO el `dailyKcalAtFull * days`
 * (que sobrestima un 30-40% según veterinario).
 */
export function getTransitionKcalTotal(schedule: TransitionDay[], dailyKcalAtFull: number): number {
  return schedule.reduce((acc, d) => acc + (dailyKcalAtFull * d.pctMyL) / 100, 0);
}

/** Energy-equivalent days of full ration the initiation pack covers: the transition
 *  days weigh their MyL share and the rest of the pack is full ration. Mirrors the
 *  backend `_effective_initiation_days`, so the funnel estimates the same reserve. */
export function getInitiationEquivalentDays(schedule: TransitionDay[], initiationDays: number): number {
  const days = Math.max(0, initiationDays);
  const capped = schedule.slice(0, days);
  const transitionEquivalent = capped.reduce((acc, day) => acc + day.pctMyL / 100, 0);
  return transitionEquivalent + Math.max(0, days - capped.length);
}

/** Convierte kcal a gramos según la densidad calórica media del catálogo. */
export function getTransitionGramsTotal(
  schedule: TransitionDay[],
  dailyKcalAtFull: number,
  avgKcalPer100g: number,
): number {
  if (avgKcalPer100g <= 0) return 0;
  const totalKcal = getTransitionKcalTotal(schedule, dailyKcalAtFull);
  return Math.round((totalKcal / avgKcalPer100g) * 100);
}

// ── Mapeo desde el funnel ──────────────────────────────────────────────────

/** Mapea las opciones multi-select de P6 (`FoodSource`) al schedule.
 *  Si el usuario marca varias dietas a la vez, escogemos la más exigente
 *  (la que genera la transición más larga: pienso seco). Esto blinda la
 *  digestión incluso si el cliente come sobre todo BARF pero también algo
 *  de pienso. */
const FOOD_SOURCE_PRIORITY: Record<string, { kind: CurrentFoodType; weight: number }> = {
  pienso_seco: { kind: 'pienso_seco', weight: 5 }, // más exigente
  comida_cocinada: { kind: 'cocinada_otra', weight: 4 }, // si hoy come cocinada (no MyL)
  natural_cocinada: { kind: 'cocinada_otra', weight: 4 },
  comida_humeda: { kind: 'humeda_comercial', weight: 3 },
  barf: { kind: 'barf', weight: 2 },
  cruda: { kind: 'cruda', weight: 2 },
  casera: { kind: 'cocinada_otra', weight: 4 },
  // 'mixta' = plato mitad pienso + mitad comida cocinada. Lo tratamos como
  // humeda_comercial (5 días) — más suave que pienso seco puro pero más
  // conservador que cocinada pura, porque el componente de pienso sigue
  // afectando al sistema digestivo durante la transición.
  mixta: { kind: 'humeda_comercial', weight: 4 },
  // 'deshidratada' = comida natural deshidratada (se rehidrata con agua
  // antes de servir). Una vez rehidratada queda muy similar a húmeda /
  // cocinada, así que tratamos su transición como humeda_comercial
  // (5 días). Si el cliente sirviera deshidratada en seco como pellets,
  // sería más restrictivo, pero el caso por defecto es rehidratada.
  deshidratada: { kind: 'humeda_comercial', weight: 4 },
  premios_sobras: { kind: 'pienso_seco', weight: 5 }, // legacy: tratar como pienso por seguridad
};

/** Mapea pathologies + subPathologies del FormContext a los modificadores
 *  específicos del schedule. La lógica:
 *  - hasDigestivePathology: SENSIBILIDAD_DIGESTIVA + (sin sub OR ibd/gastritis)
 *  - hasChronicSoftStools: SENSIBILIDAD_DIGESTIVA + sólo heces_blandas
 *  Si las dos aplican (heces blandas + IBD), digestiva gana (más restrictiva).
 */
export function deriveDigestiveModifiers(
  pathologies: string[] | undefined | null,
  subPathologies: Record<string, string[]> | undefined | null,
): Pick<TransitionModifiers, 'hasDigestivePathology' | 'hasChronicSoftStools'> {
  const sensiblity = (pathologies || []).some((p) => p === 'sensibilidad_digestiva');
  if (!sensiblity) return {};
  const subs = subPathologies?.sensibilidad_digestiva || [];
  const hasIBDOrGastritis = subs.includes('ibd') || subs.includes('gastritis_reflujo');
  const hasSoftStools = subs.includes('heces_blandas');
  if (hasIBDOrGastritis) {
    return { hasDigestivePathology: true };
  }
  if (hasSoftStools) {
    return { hasChronicSoftStools: true };
  }
  // Padre marcado sin sub específico → conservador (digestivo full).
  return { hasDigestivePathology: true };
}

export function pickCurrentFoodType(currentFood: string[] | undefined | null): CurrentFoodType {
  if (!currentFood || currentFood.length === 0) {
    // Sin info → asumimos pienso seco (caso más conservador, transición más larga).
    return 'pienso_seco';
  }
  const candidates = currentFood
    .map((f) => FOOD_SOURCE_PRIORITY[f])
    .filter((c): c is { kind: CurrentFoodType; weight: number } => Boolean(c));
  if (candidates.length === 0) return 'pienso_seco';
  candidates.sort((a, b) => b.weight - a.weight);
  return candidates[0].kind;
}

/** Detalle día a día en gramos — útil para mostrar la guía en el email/PDF. */
export function getTransitionGramsByDay(
  schedule: TransitionDay[],
  dailyKcalAtFull: number,
  avgKcalPer100g: number,
): Array<{ day: number; pctMyL: number; gramsMyL: number; gramsOldDiet: number; kcalMyL: number }> {
  if (avgKcalPer100g <= 0) {
    return schedule.map((d) => ({
      day: d.day,
      pctMyL: d.pctMyL,
      gramsMyL: 0,
      gramsOldDiet: 0,
      kcalMyL: 0,
    }));
  }
  return schedule.map((d) => {
    const kcalMyL = (dailyKcalAtFull * d.pctMyL) / 100;
    const kcalOld = dailyKcalAtFull - kcalMyL;
    return {
      day: d.day,
      pctMyL: d.pctMyL,
      gramsMyL: Math.round((kcalMyL / avgKcalPer100g) * 100),
      // El kcal/100g del pienso/comida vieja varía mucho; aquí asumimos
      // 350 kcal/100g típico de pienso seco como referencia. La UI debería
      // adaptarlo al caso (humeda ≈ 100, BARF ≈ 150).
      gramsOldDiet: Math.round((kcalOld / 350) * 100),
      kcalMyL: Math.round(kcalMyL),
    };
  });
}
